import {
  Box,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import VisibilityIcon from "@mui/icons-material/Visibility";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import DataTable from "../../components/datatable/DataTable";
import FilterBar from "../../components/common/FilterBar";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getNotifications, markNotificationRead } from "../../api/api";

const Notifications = () => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // ================= FETCH =================
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await getNotifications();
      setNotifications(res.data?.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // ================= FILTER =================
  // ================= FILTER =================
  const filteredData = notifications.filter((item) => {
    const msg = item.message?.toLowerCase() || "";

    const matchSearch = msg.includes(search.toLowerCase());

    let matchType = true;

    switch (type) {
      case "login":
        matchType = item.title === "New Lead Added";
        break;

      case "pending":
        matchType = item.title === "Lead Marked Pending";
        break;

      case "credit":
        matchType =
          item.title === "Lead Status Updated" &&
          msg.includes("moved to credit");
        break;

      case "approved":
        matchType = item.type === "loan";
        break;

      case "disbursed":
        matchType = item.type === "disbursement";
        break;

      default:
        matchType = true;
    }

    return matchSearch && matchType;
  });
  // ================= NAVIGATION =================
  const handleNavigate = async (row: any) => {
    try {
      // 🔔 mark as read
      if (!row.isRead) {
        await markNotificationRead(row._id);
      }

      // 🔥 REDIRECT BASED ON TYPE
      if (row.type === "lead" && row.meta?.leadId?._id) {
        navigate(`/leads/view/${row.meta.leadId._id}`);
      } else if (row.type === "loan" && row.meta?.loanId?._id) {
        navigate(`/loans/view/${row.meta.loanId._id}`);
      } else if (row.type === "disbursement" && row.meta?.loanId?._id) {
        navigate(`/disbursement/view/${row.meta.loanId._id}`);
      }

      // refresh list
      fetchNotifications();
    } catch (err) {
      console.log(err);
    }
  };

  // ================= COLUMNS =================
  const columns: GridColDef[] = [
    {
      field: "message",
      headerName: "Notification",
      flex: 1,
      minWidth: 350,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            overflow: "hidden",
          }}
        >
          {!params.row.isRead && (
            <NotificationsActiveIcon
              sx={{
                fontSize: 16,
                color: "#f59e0b",
              }}
            />
          )}

          <Typography
            sx={{
              fontSize: 13,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {params.row?.meta?.leadId?.leadCode
              ? `${params.row.meta.leadId.leadCode} - `
              : ""}
            {params.value}
          </Typography>
        </Box>
      ),
    },

    {
      field: "type",
      headerName: "Type",
      width: 120,
      renderCell: (params) => {
        const value = params.value;

        let bg = "#eef2ff";
        let color = "#6366f1";

        if (value === "loan") {
          bg = "#dcfce7";
          color = "#16a34a";
        }

        if (value === "disbursement") {
          bg = "#fff7ed";
          color = "#ea580c";
        }

        if (value === "lead") {
          bg = "#e0f2fe";
          color = "#0284c7";
        }

        return (
          <Chip
            label={value}
            size="small"
            sx={{
              background: bg,
              color: color,
              textTransform: "capitalize",
              fontWeight: 500,
            }}
          />
        );
      },
    },

    {
      field: "isRead",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Read" : "Unread"}
          size="small"
          sx={{
            background: params.value ? "#dcfce7" : "#fef3c7",

            color: params.value ? "#16a34a" : "#d97706",

            fontWeight: 500,
          }}
        />
      ),
    },

    {
      field: "createdAt",
      headerName: "Date",
      width: 190,
      renderCell: (params) => new Date(params.value).toLocaleString("en-IN"),
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View">
          <IconButton
            size="small"
            onClick={() => handleNavigate(params.row)}
            sx={{
              background: "#eef2ff",
              color: "#6366f1",

              "&:hover": {
                background: "#e0e7ff",
              },
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: 1 }}>
      {/* 🔥 FILTER */}
      <FilterBar
        title="Notifications"
        search={search}
        onSearchChange={setSearch}
        status={type}
        onStatusChange={setType}
      />
      ß{/* 🔥 TABLE */}
      <Paper
        sx={{
          mt: 2,
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0",
        }}
      >
        <DataTable rows={filteredData} columns={columns} loading={loading} />
      </Paper>
    </Box>
  );
};

export default Notifications;
